import { forwardRef, useState, useRef, useImperativeHandle, useEffect } from 'react';
import { useUi } from '@weareconceptstudio/core';

//* Styles
import VideoStyle from './style';

//* Lottie file
import play from '@/lottie/play.json';

//* Components
import LottieAnimation from '../LottieAnimation';

const Video = forwardRef(({ isIcon = true, double, onLoad, ...props }, ref) => {
	const { winWidth } = useUi();

	//! Refs
	const videoRef = useRef();

	//! Imperative Handle
	useImperativeHandle(ref, () => videoRef.current, []);

	//! Source Selector
	const doubleSrc = double ? (winWidth >= 1280 ? double?.[0]?.src : double?.[1]?.src) : null;

	//! States
	const [isPlaying, setIsPlaying] = useState(false);
	const [showControls, setShowControls] = useState(true);
	const [loaded, setLoaded] = useState(false);
	const [ended, setEnded] = useState(false);

	//! Get video element helper
	const getVideo = () => videoRef.current?.querySelector('video');

	//! Toggle Play/Pause
	const togglePlay = () => {
		const video = getVideo();
		if (!video) return;

		if (video.paused || video.ended) {
			video.play();
			setIsPlaying(true);
			setEnded(false);
		} else {
			video.pause();
			setEnded(true);
			setShowControls(true);
		}
	};

	//! Handle Mouse Enter/Leave
	const handleMouseEnter = () => {
		setShowControls(true);
	};

	const handleMouseLeave = () => {
		if (isPlaying && !ended) {
			setShowControls(false);
		}
	};

	//! Handle video loaded
	const onLoadCallback = () => {
		setLoaded(true);
	};

	useEffect(() => {
		if (typeof onLoad === 'function') {
			onLoad(loaded);
		}
	}, [loaded, onLoad]);

	//! Attach "ended" event once
	useEffect(() => {
		const video = getVideo();
		if (!video) return;

		const handleEnded = () => {
			setShowControls(true);
			setEnded(true);
		};

		video.addEventListener('ended', handleEnded);

		return () => {
			video.removeEventListener('ended', handleEnded);
		};
	}, []);

	//! Optional combined click handler
	const handleVideoClick = (e) => {
		if (props.handleClick) props.handleClick(e);
		togglePlay();
	};

	return (
		<VideoStyle
			ref={videoRef}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			className={`video-cont ${props.className || ''}`}
		>
			<video
				playsInline
				muted={props.muted}
				autoPlay={props.autoPlay}
				loop={props.loop}
				preload='metadata'
				controls={props.controls}
				onClick={handleVideoClick}
				onCanPlay={onLoadCallback}
			>
				<source
					src={`${props?.src || doubleSrc}#t=0.1`}
					type='video/mp4'
				/>
				<source
					src={`${props?.src || doubleSrc}#t=0.1`}
					type='video/webm'
				/>
				Your browser does not support the video tag.
			</video>

			{isIcon && (
				<div
					className={`play-pause-block ${showControls ? 'active' : ''}`}
					onClick={togglePlay}
				>
					<LottieAnimation
						autoplay={false}
						isPlay={isPlaying}
						isReverse={ended}
						loop={false}
						animData={play}
						render='svg'
						// speed={2}
						// duration={1}
					/>
				</div>
			)}
		</VideoStyle>
	);
});

Video.displayName = 'Video';

export default Video;
